import axios from 'axios';

import buildQuery from 'odata-query';
import { IRequestConfig, MethodsTypes } from 'models/Axios';
import { IUserData, IUser, UserRoleTypes, UserProfileTypes, UserType, DeleteCustomerRoleUserProps } from 'models/User';
import Services from './index.service';
import { ContactPresidentProps } from 'components/ContactPresident/interface';
import { UserFilter } from 'models/UserFilter';

export default class UserService extends Services<IUserData, IUser> {


  public async setDefaultAtributtes(userId: string) {

    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/pipefy/set-default-atributtes`,
      method: MethodsTypes.post,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      data: {
        id: userId
      }
    };

    await axios(config);
  }
  /**
   * Method to get current user info
   */
  public async currentUser() {
    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/current-user`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    return await axios(config);
  }

  /**
   * Method to get the pipefy token for current user
   */
  public async getPipefyToken(): Promise<string> {
    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/pipefy/get-token-v2`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    const response = await axios(config);

    return response.data.token as string;
  }

  /**
   * Method to get the pipefy token for current user
   */
  public async getZenviaToken(): Promise<string> {
    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/zenvia/get-token`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    const response = await axios(config);

    return response.data.token as string;
  }

  /**
   * Method to get users without Odata
   */
  public filter = (
    currentPage?: number,
    pageSize?: number,
    filter?: UserFilter,
    ordination?: {
      field: number;
      direction: number;
    }
  ) => {
    const config: IRequestConfig<IUser> = {
      url: `/users/filter`,
      params: {
        search: filter?.search,
        userType: filter?.userType,
        profileType: filter?.profileType,
        deleted: filter?.deleted ?? false,
        enabled: filter?.enabled,
        supplierId: filter?.supplierId,
        pageSize: pageSize || 20,
        pageNumber: currentPage || 0,
        sortField: ordination?.field,
        sortDirection: ordination?.direction,
        customerId: filter?.customerId,
        pending: filter?.pending
      },
    };

    return this.httpRequest(config);
  };

  /**
   * Method to get users
   */
  public get = (
    currentPage?: number,
    pageSize?: number,
    orderby?: string,
    filter?: {}
  ) => {
    const skip = currentPage || 0;
    const top = pageSize || 20;
    const order = orderby ? `${orderby}` : undefined;
    const filterQuery = buildQuery({ filter });

    const config: IRequestConfig<IUser> = {
      url: `/users`,
      params: {
        $top: `${top}`,
        $skip: `${skip * top}`,
        $orderby: order,
        $filter: filterQuery.split('=')[1] || undefined,
      },
    };

    return this.httpRequest(config);
  };

  /**
   * Method to invite new user
   * @param {string} email
   * @param {Array<number>} roles
   * @param {number} profileType
   */
  public async invite(
    email?: string,
    roles?: Array<keyof typeof UserRoleTypes>,
    profileType?: Array<keyof typeof UserProfileTypes>,
    customerId?: string,
    forceInvite?: boolean
  ) {
    const data = {
      email,
      roles,
      profileType,
      customerId,
      forceInvite,
    };

    return await axios
      .post(`${this.base_url}/users/invite`, data, {
        headers: { Authorization: `Bearer ${this.access_token()}` },
      })
      .then((result) => result)
      .catch((err) => err);
  }

  /**
   * Method to register new user
   * @param {string} email
   * @param {string} fullName
   * @param {string} phoneNumber
   * @param {string} password
   * @param {string} token
   */
  public async register(
    email: string,
    fullName: string,
    phoneNumber: string,
    password: string,
    token: string,
    supplierId: string
  ) {
    const data = {
      email,
      fullName,
      phoneNumber,
      password,
      token,
      supplierId
    };

    return await axios
      .put(`${this.base_url}/users/register-user`, data)
      .then((result) => result)
      .catch((err) => err);
  }

  /**
   * Method to update user infos
   * @param data Object with user infos
   */
  public async update(data: any) {
    return await axios.put(`${this.base_url}/users/profile`, data, {
      headers: { Authorization: `Bearer ${this.access_token()}` },
    });
  }

  public async deleteCustomerRoleUser(data: DeleteCustomerRoleUserProps) {
    const config: IRequestConfig<IUser> = {
      baseUrl: this.base_url,
      method: MethodsTypes.delete,
      url: `/users/delete-permisson-customer`,
      params: {
        userId: data.userId,
        customerId: data.customerId
      },
    };
    return this.httpRequest(config);
  }

  public async deleteCustomer(data: DeleteCustomerRoleUserProps) {
    const config: IRequestConfig<IUser> = {
      baseUrl: this.base_url,
      method: MethodsTypes.delete,
      url: `/users/delete-customer`,
      params: data,
    };
    return this.httpRequest(config);
  }

  /**
 * Method to delete user
 * @param {string} id
 */
  public async delete(id: string) {
    await axios.delete(`${this.base_url}/users/${id}`, {
      headers: { Authorization: `Bearer ${this.access_token()}` },
    });
  }

  /**
   * Method to send user reset password token to user email
   * @param email user email
   */
  public async resetPassword(email: string) {
    const data = {
      email,
    };

    return axios.post(`${this.base_url}/authentication/new-password`, data);
  }

  /**
   * Method to send user reset password token to user email
   * @param token user token provided from email
   * @param password user new password
   */
  public async changePassword(token: string, password: string) {
    const data = {
      token,
      password,
    };

    return axios.post(`${this.base_url}/authentication/change-password`, data);
  }

  /**
   * Register the user
   * Uses, fullName, e-mail, roles, profileType, BUs and brands
   * @param data Object with user infos
   */
  public async registerEmployee(user: IUser) {
    const data = user;

    // only ids
    let arrayAux: Array<any> = [];

    data.businessUnits?.map(({ id }) => arrayAux.push(id));
    data.businessUnits = arrayAux;

    arrayAux = [];
    data.brands?.map(({ id }) => arrayAux.push(id));
    data.brands = arrayAux;

    arrayAux = [];
    data.subsidiaries?.map(({ id }) => arrayAux.push(id));
    data.subsidiaries = arrayAux;

    if (data.id) {
      return await axios
        .put(`${this.base_url}/users/alter_employee`, data, {
          headers: { Authorization: `Bearer ${this.access_token()}` },
        })
        .then((result) => result)
        .catch((err) => err);
    }

    return await axios
      .post(`${this.base_url}/users/register_employee`, data, {
        headers: { Authorization: `Bearer ${this.access_token()}` },
      })
      .then((result) => result)
      .catch((err) => err);
  }

  public async keycloakAutoComplete(str: string) {
    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/keycloak/find`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      params: {
        userName: str,
      },
    };

    return await axios(config)
      .then((result) => result)
      .catch((err) => []);
  }

  /**
   * Method to update user last access   *
   */
  public async updateLastLogin() {
    return await axios.put(
      `${this.base_url}/users/update-last-login`,
      undefined,
      {
        headers: { Authorization: `Bearer ${this.access_token()}` },
      }
    );
  }

  public async setPipefyToken(token: string, redirectUri: string) {
    return await axios.put(
      `${this.base_url}/users/pipefy/set-token`,
      {
        code: token,
        redirect_uri: redirectUri,
      },
      {
        headers: { Authorization: `Bearer ${this.access_token()}` },
      }
    );
  }

  public async autoComplete(str: any, userType: UserType = UserType.Employee) {

    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/autocomplete?sentence=${str}&userType=${userType}`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    return await axios(config)
      .then((result) => result.data)
      .catch(() => []);
  }

  public sendContactPresident(
    files: Array<File>,
    data: ContactPresidentProps) {
    const formData = new FormData();
    files.forEach(file => formData.append('file', file));

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    return axios
      .post(`${this.base_url}/users/talk-president?customerId=
            ${data.customerId}&name=${data.name}
            &enterpriseName=${data.enterpriseName}&email=${data.email}&phone=${data.phone}
            &subject=${data.subject}&message=${data.message}`, formData, {
        headers: {
          Authorization: `Bearer ${this.access_token()}`,
          'Content-Type': 'multipart/form-data',
        }
      });
  }

  public async clearPipefyToken(userId: string) {

    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/clear-pipefy-token`,
      method: MethodsTypes.post,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      data: {
        id: userId
      }
    };

    await axios(config);
  }

  public async updateUserOnPipefy(newEmail: string, oldEmail: string) {

    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/update-user-on-pipefy`,
      method: MethodsTypes.put,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      data: {
        email: oldEmail,
        newEmail: newEmail
      }
    };

    await axios(config);
  }

  public async getEditPermissions(values: { id: string, email: string, pipefyLink: string; }) {

    const objParams = {
      id: values.id,
      email: values.email,
      pipefyLinkType: values.pipefyLink
    };

    const config: IRequestConfig<any> = {
      url: `${this.base_url}/users/edit-permissions`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      params: objParams
    };

    try {
      const response = await axios(config);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch edit permissions");
    }
  }

}
